/**
 * The maker's brand, as CSS.
 *
 * This app is sold to kitchen makers who put it on their own site under their own name, so
 * the one thing that must never be hard-coded is what it looks like. It was, though: thirty
 * hex values spread across two components, every one of them Harbourside's -- which meant the
 * second maker was a find-and-replace through a stylesheet rather than a line of data.
 *
 * A maker supplies FOUR colours. Everything else is derived from them, because a brand is a
 * decision about ink and paper and a maker should not have to nominate a value for the
 * hairline between two rows of swatches.
 *
 * `color-mix` does the deriving, in oklab, so a mix of a dark ink into a warm paper stays
 * warm instead of going through grey the way a naive sRGB blend does.
 */

/** The four a maker gives us, and what happens if they leave one out. */
const FALLBACK = {
  ink: '#1C1917',      // text, and the primary button
  paper: '#FAF8F5',    // the sheet the controls stand on
  accent: '#9A6B3F',   // theirs, used on nothing decorative
  room: '#ECE7E0',     // behind the kitchen, and the 3D scene's background
  warn: '#8A3E1E',     // a refusal, and the one destructive action
}

export function brandOf(maker) {
  return { ...FALLBACK, ...(maker?.brand ?? {}) }
}

/**
 * Write the brand onto an element as custom properties.
 *
 * On `document.documentElement` by default rather than the page component, because the
 * sign-off modal renders into the same tree and a `position: fixed` overlay inherits from
 * where it SITS in the DOM -- which is easy to get wrong once and never notice, since it
 * looks right until somebody moves the component.
 */
export function applyBrand(maker, el = document.documentElement) {
  const b = brandOf(maker)
  const mix = (pct) => `color-mix(in oklab, ${b.ink} ${pct}%, ${b.paper})`
  const vars = {
    '--k-ink': b.ink,
    '--k-paper': b.paper,
    '--k-accent': b.accent,
    '--k-room': b.room,
    '--k-warn': b.warn,
    // Derived. The percentages are the recipe's contrast ladder, not taste: `--k-dim` is
    // still readable outdoors, `--k-mute` is the quietest thing allowed to carry a word, and
    // anything below that may only be a line or a fill.
    '--k-dim': mix(62),
    '--k-mute': mix(45),
    '--k-line': mix(12),
    '--k-fill': mix(7),
    '--k-sunk': mix(10),
  }
  for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v)
  return vars
}
